/**
 * The board's key, in two parts that are always on the page:
 *
 *   BoardLegend   a Tide Living Legend keyed by the tab colours: swatch +
 *                 label + a non-colour marker + what the colour means + the
 *                 count from the same predicate as the tab. Static: the tabs
 *                 are the control; the legend explains them.
 *   TabsBasis     one sentence saying what the tabs ARE right now (Monday
 *                 groups as of a mirror run, or Status words, and why), with
 *                 the failed-sync caveat when the mirror's last run failed.
 *   ColumnKey     every column's meaning and source, as a disclosure list, so
 *                 nothing a header says lives only in a hover title.
 */
import React from 'react';
import { LivingLegend } from '../tide';
import type { LegendItem } from '../tide';
import type { BoardColumn, BoardPage, BoardTabDef } from './types';
import { boardStamp, tabSourceText } from './format';

/** `explain`: describe each item by its tab's `what` (a definition) instead of its source. */
export function legendItems(tabs: BoardTabDef[], { explain = false, units = ['row', 'rows'] }: { explain?: boolean; units?: [string, string] } = {}): LegendItem[] {
  return tabs
    .filter((t) => t.key !== 'all' && (t.color || t.source !== 'EMERGE'))
    .filter((t) => !(t.hide_when_empty && (t.count ?? 0) === 0))
    .map((t) => ({
      key: t.key,
      label: t.label,
      color: t.color || 'var(--tide-status-missing)',
      marker: t.marker || (t.color ? (t.source === 'EMERGE' ? 'diamond' : 'square') : 'hatch'),
      // A status word's source is said once, in the scope line; an item only explains what differs (a lens, a group's run, a missing colour).
      description: explain ? t.what : [t.source === 'STATUS' ? null : tabSourceText(t), t.color ? null : 'Monday colour not recorded'].filter(Boolean).join(' · ') || undefined,
      value: typeof t.count === 'number' ? t.count : null,
      unit: t.count === 1 ? units[0] : units[1],
    }));
}

export default function BoardLegend({ title, tabs, scope, explain = false, units }: { title: string; tabs: BoardTabDef[]; scope?: string; explain?: boolean; units?: [string, string] }) {
  const items = legendItems(tabs, { explain, units });
  if (!items.length) return null;
  return (
    <div className="board-legend">
      <LivingLegend
        title={title}
        items={items}
        mode="static"
        density="compact"
        scope={scope || "Each colour is the board's own Monday colour, drawn as a bar beside the word; words are the board's Status words unless marked. Counts follow your search and filters."}
      />
    </div>
  );
}

const REASON: Record<string, string> = {
  NO_POOL: 'eMerge has no read connection to the Monday mirror on this server.',
  NO_COMPLETED_RUN: 'the Monday mirror has not completed a read of this board yet.',
  NO_GROUPS: 'the Monday mirror does not carry this board\'s groups yet.',
  UNAVAILABLE: 'the Monday mirror could not be read just now.',
};

export function TabsBasis({ page }: { page: Pick<BoardPage<unknown>, 'mode' | 'groups'> }) {
  if (page.mode === 'GROUP') {
    const at = boardStamp(page.groups.as_of) || 'an unrecorded time';
    return (
      <>
        <p className="board-basis">Tabs are the board&rsquo;s Monday groups, read from the Monday mirror as of {at}. Status stays a column.</p>
        {page.groups.sync_failed && (
          <p className="board-caveat" role="note">Last Monday sync of this board failed; groups as of {at}.</p>
        )}
      </>
    );
  }
  return (
    <p className="board-basis">
      Tabs are the board&rsquo;s Status words. Monday groups replace them here once the Monday mirror carries them
      {REASON[page.groups.state] ? <> — for now, {REASON[page.groups.state]}</> : '.'}
    </p>
  );
}

export function ColumnKey<T>({ columns, identity, notes = [] }: { columns: Array<Pick<BoardColumn<T>, 'key' | 'label' | 'help'>>; identity?: { label: string; help: string }; notes?: string[] }) {
  return (
    <details className="board-colkey">
      <summary>What each column means, and where it comes from</summary>
      <dl>
        {identity && (<><dt>{identity.label}</dt><dd>{identity.help}</dd></>)}
        {columns.map((c) => (<React.Fragment key={c.key}><dt>{c.label}</dt><dd>{c.help}</dd></React.Fragment>))}
      </dl>
      {notes.map((n) => <p key={n} className="board-colkey__note">{n}</p>)}
    </details>
  );
}
